"use client";

import { useCallback } from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { Pagination } from "@/components/backoffice/pagination";
import { NoResult } from "../no-result";
import { DraftSummaryCard } from "./draft-summary-card";
import { useDraftsFilters } from "@/hooks/cat/use-drafts-filters";
import {
  invalidateDraftsListQuery,
  useDraftsListQuery,
} from "@/lib/queries/cat/drafts";
import { deleteDraftAction } from "@/app/lib/actions/cat/draft-delete";
import { resumeDraftAction } from "@/app/lib/actions/cat/draft-resume";
import { toast } from "sonner";
import type { DraftListItemViewModel } from "@/types/cat/drafts";
import {
  formatDraftClientLabel,
  formatDraftVehicleLabel,
} from "@/lib/cat/draft-list-mapper";

function DraftListResults() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { q, p, s, setFilters } = useDraftsFilters();

  const listQuery = useDraftsListQuery({ q, p, s }, undefined, {
    refetchInterval: (query) => {
      if (
        typeof document === "undefined" ||
        document.visibilityState !== "visible"
      ) {
        return false;
      }
      const hasLocked =
        query.state.data?.items.some((item) => item.isLockedByOther) ?? false;
      return hasLocked ? 15_000 : 30_000;
    },
    refetchOnWindowFocus: true,
  });

  const { data, isPending, isFetching } = listQuery;
  const items = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  const handlePageChange = useCallback(
    (params: { page: number; size: number }) => {
      setFilters({ p: params.page, s: params.size });
    },
    [setFilters],
  );

  const handleResume = useCallback(
    async (draftId: number) => {
      const result = await resumeDraftAction({ draftId });
      if (!result.success) {
        if (result.code === "LOCKED") {
          toast.error(
            result.message ??
              `Brouillon en cours d'édition par ${result.editorName ?? "un autre utilisateur"}.`,
          );
          invalidateDraftsListQuery(queryClient);
          return;
        }
        toast.error(result.message ?? "Impossible de reprendre le brouillon.");
        return;
      }

      if (!result.data) {
        toast.error("Aucune donnée de reprise n'a été retournée.");
        return;
      }

      router.push(
        `/backoffice/cat/vehicle-assignment/retail?draftId=${draftId}&step=${result.data.stepIndex}`,
      );
    },
    [queryClient, router],
  );

  const handleDelete = useCallback(
    async (draftId: number) => {
      const result = await deleteDraftAction({ draftId });
      if (!result.success) {
        toast.error(result.message ?? "Impossible de supprimer le brouillon.");
        return;
      }
      toast.success("Brouillon supprimé.");
      invalidateDraftsListQuery(queryClient);
    },
    [queryClient],
  );

  if (isPending) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <Skeleton key={index} className="h-48 w-full" />
        ))}
      </div>
    );
  }

  if (q && items.length === 0) {
    return <NoResult query={q} />;
  }

  if (items.length === 0) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        Aucun brouillon en cours.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {isFetching ? (
        <p className="text-muted-foreground text-xs">Actualisation…</p>
      ) : null}

      {items.map((item) => (
        <DraftListCard
          key={item.id}
          item={item}
          onDelete={() => void handleDelete(item.id)}
          onResume={() => void handleResume(item.id)}
        />
      ))}

      <Pagination
        page={p}
        size={s}
        totalItems={totalCount}
        onChange={handlePageChange}
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </div>
  );
}

function DraftListCard({
  item,
  onResume,
  onDelete,
}: {
  item: DraftListItemViewModel;
  onResume: () => void;
  onDelete: () => void;
}) {
  const vehicle = formatDraftVehicleLabel(item);
  const client = formatDraftClientLabel(item);

  return (
    <DraftSummaryCard
      name={`DRAFT-${item.id}`}
      kvps={item.kvps}
      vehiclePrimaryLine={vehicle.primaryLine}
      vehicleModelLine={vehicle.modelLine}
      vehicleVinLine={vehicle.secondaryLine}
      client={client}
      clientUnset={client === "Client non renseigné"}
      lastModified={new Date(item.updatedAt)}
      daysRemaining={item.daysRemaining}
      expiringSoon={item.expiringSoon}
      currentStep={item.progressCompleted}
      steps={item.progressTotal}
      author={item.authorName ?? "Utilisateur inconnu"}
      createdAt={new Date(item.createdAt)}
      isLockedByOther={item.isLockedByOther}
      editorName={item.editorName}
      onEdit={onResume}
      onDelete={onDelete}
    />
  );
}

export { DraftListResults };
